<template>
	<div style="width: 100%" class="d-flex justify-end">
		<!-- Use textarea for single values (auto-grow support) -->

		<v-textarea
			v-if="!compProps.isArray && datatype.id === 'string'"
			class="question-input-field"
			ref="textarea"
			v-model="textareaValue"
			style="width: 100%"
			variant="solo-filled"
			density="comfortable"
			clearable
			auto-grow
			rows="1"
			hide-details="auto"
			:label="compProps.label"
			:placeholder="compProps.placeholder"
			:autofocus="compProps.autofocus"
			:class="{
				'highlight-drop': questionsStore.suggestionDrag.isDragged && datatypesAreCompatible(),
				'text-input--unknown-active': isUnknown,
				novaBotAnimationInput:
					questionsStore.tab === 'open' &&
					alternisNewStore.useNewAlternis &&
					alternisNewStore.isVoicebot &&
					textareaValue &&
					textareaValue.length > 0,
			}"
			@click:clear="onTextareaClear"
			@update:focused="onUpdateFocused"
			@keydown.enter="onTextareaEnter"
			@drop="onDrop"
			@dragover="onDragOver"
		>
			<template v-if="showSuggestion" #details>
				<!-- Short suggestions: use chip -->
				<v-chip
					v-if="!isMultiLineSuggestion"
					class="nta-border suggestion-chip"
					:style="{ '--suggestion-chip-color': suggestionChipColor }"
					density="comfortable"
					draggable
					@dragstart="dragStart"
					@dragend="dragEnd"
					@click="setValueFromSuggestion"
					>{{ suggestion }}</v-chip
				>
				<!-- Long suggestions: use styled box -->
				<div
					v-else
					class="suggestion-box nta-border"
					:style="{ '--suggestion-chip-color': suggestionChipColor }"
					draggable="true"
					@dragstart="dragStart"
					@dragend="dragEnd"
					@click="setValueFromSuggestion"
				>
					{{ suggestion }}
				</div>
			</template>
			<template v-if="debugStore.altKeyHeld || debugStore.debugMode" #append>
				<v-tooltip :text="symbolsStore.GUILabel('6fb730ba-a492-48c6-9eb2-64045fea443d')" location="top">
					<template v-slot:activator="{ props }">
						<v-btn
							v-if="
								domainStore.hasAccessRights(UserRole.Editor) &&
								(debugStore.altKeyHeld || debugStore.debugMode)
							"
							v-bind="props"
							size="small"
							color="blue"
							class="ml-1"
							icon="mdi-link"
							variant="text"
							@click="openEngineerDataobject"
						>
						</v-btn>
					</template>
				</v-tooltip>
			</template>
		</v-textarea>

		<!-- Use combobox for arrays (chips support) -->
		<!-- date || time || timestamp || number -->
		<v-combobox
			v-else
			class="question-input-field"
			ref="combobox"
			v-model="displayedValue"
			style="width: 100%"
			variant="solo-filled"
			density="comfortable"
			clearable
			closable-chips
			hide-details="auto"
			hide-no-data
			:type="datatype.id === 'timestamp' ? 'datetime-local' : datatype.id"
			:label="compProps.label"
			:placeholder="compProps.placeholder"
			:multiple="compProps.isArray"
			:chips="compProps.isArray"
			:autofocus="compProps.autofocus"
			:class="{
				'question-input-field--multi': compProps.isArray,
				'highlight-drop': questionsStore.suggestionDrag.isDragged && datatypesAreCompatible(),
				'text-input--unknown-active': isUnknown,
				novaBotAnimationInput:
					questionsStore.tab === 'open' &&
					alternisNewStore.useNewAlternis &&
					alternisNewStore.isVoicebot &&
					displayedValue &&
					displayedValue.length > 0,
			}"
			@click:clear="setValue('undefined')"
			@update:focused="onUpdateFocused"
			@change="onComboboxChange"
			@keydown="onComboboxKeydown"
			@keydown.enter="onComboboxEnter"
			@drop="onDrop"
			@dragover="onDragOver"
			@update:search="repairSearch"
		>
			<template v-if="showSuggestion" #details>
				<!-- Short suggestions: use chip -->
				<v-chip
					v-if="!isMultiLineSuggestion"
					class="nta-border suggestion-chip"
					:style="{ '--suggestion-chip-color': suggestionChipColor }"
					density="comfortable"
					draggable
					@dragstart="dragStart"
					@dragend="dragEnd"
					@click="setValueFromSuggestion"
					>{{ suggestion }}</v-chip
				>
				<!-- Long suggestions: use styled box -->
				<div
					v-else
					class="suggestion-box nta-border"
					:style="{ '--suggestion-chip-color': suggestionChipColor }"
					draggable="true"
					@dragstart="dragStart"
					@dragend="dragEnd"
					@click="setValueFromSuggestion"
				>
					{{ suggestion }}
				</div>
			</template>
			<template v-if="debugStore.altKeyHeld || debugStore.debugMode" #append>
				<v-tooltip :text="symbolsStore.GUILabel('6fb730ba-a492-48c6-9eb2-64045fea443d')" location="top">
					<template v-slot:activator="{ props }">
						<v-btn
							v-if="
								domainStore.hasAccessRights(UserRole.Editor) &&
								(debugStore.altKeyHeld || debugStore.debugMode)
							"
							v-bind="props"
							size="small"
							color="blue"
							class="ml-1"
							icon="mdi-link"
							variant="text"
							@click="openEngineerDataobject"
						>
						</v-btn>
					</template>
				</v-tooltip>
			</template>
		</v-combobox>
	</div>
</template>

<script setup lang="ts">
import type { DescriptorDatatype, InstanceTask } from "@lr-notitia/notitia-operator/schemas";
import { ref, computed, PropType, onMounted, onUnmounted, watch } from "vue";
import { useQuestionsStore } from "../../../../store/questions";
import { useGraphStore } from "../../../../store/graph";
import { useGptSuggestionsStore } from "../../../../store/gpt/suggestions";
import { useSymbolsStore } from "../../../../store/symbols";
import { useDomainStore } from "../../../../store/domain";
import { useDebugStore } from "../../../../store/debug";
import { useAlternisNewStore } from "../../../../store/alternis_new";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import { ConceptView } from "@lr-notitia/notitia-model-store/schemas";

const questionsStore = useQuestionsStore();
const graphStore = useGraphStore();
const gptSuggestionsStore = useGptSuggestionsStore();
const symbolsStore = useSymbolsStore();
const domainStore = useDomainStore();
const debugStore = useDebugStore();
const alternisNewStore = useAlternisNewStore();

const emits = defineEmits(["set"]);
const compProps = defineProps({
	datatype: {
		type: Object as PropType<DescriptorDatatype>,
		required: true,
	},
	value: {
		type: String as PropType<string | string[]>,
		required: true,
	},
	isArray: {
		type: Boolean,
		required: true,
	},
	label: {
		type: String,
		required: false,
	},
	placeholder: {
		type: String,
		required: false,
	},
	autofocus: {
		type: Boolean,
		required: false,
	},
	instanceTask: {
		type: Object as PropType<InstanceTask | undefined>,
		required: false,
		default: undefined,
	},
	attributeId: {
		type: String,
		required: false,
		default: "",
	},
	questionColor: {
		type: String,
		required: false,
		default: undefined,
	},
});

const combobox = ref<any>(null);
const textarea = ref<any>(null);

// Separate ref for textarea to enable immediate updates for auto-grow
const textareaValue = ref<string | undefined>(undefined);

// Sync textareaValue with prop value
watch(
	() => compProps.value,
	(newVal) => {
		if (newVal === "undefined" || newVal === "unknown") {
			textareaValue.value = undefined;
		} else {
			textareaValue.value = String(newVal);
		}
	},
	{ immediate: true },
);

const attribute = computed(() => {
	return graphStore.instance.attribute.byId(compProps.attributeId);
});
const displayedValue = computed({
	get: () => {
		if (compProps.value === "undefined" || compProps.value === "unknown") return undefined;

		// Convert timestamp from milliseconds to datetime-local string
		if (compProps.datatype.id === "timestamp" && typeof compProps.value === "number") {
			const date = new Date(compProps.value);
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			const hours = String(date.getHours()).padStart(2, "0");
			const minutes = String(date.getMinutes()).padStart(2, "0");

			return `${year}-${month}-${day}T${hours}:${minutes}`;
		}

		if (!compProps.isArray) return String(compProps.value);
		return compProps.value;
	},
	set: (v) => {
		lastTypedValue.value = v;

		if (compProps.isArray) {
			if (v === undefined || v === null || (Array.isArray(v) && v.length === 0)) {
				debounce(() => setValue("undefined"));
				return;
			}
			if (Array.isArray(v)) {
				debounce(() => setValue(v));
			}
			return;
		}

		if (
			compProps.datatype.id === "string" ||
			compProps.datatype.id === "number" ||
			compProps.datatype.id === "date" ||
			compProps.datatype.id === "time" ||
			compProps.datatype.id === "timestamp"
		)
			return;
		if (v === undefined || v === null || v === "" || v.length === 0) debounce(() => setValue("undefined"));
		else debounce(() => setValue(v));
	},
});

const lastTypedValue = ref();
const isKeyboardDateEntry = ref(false);
const suggestion = computed(() => {
	if (!compProps.instanceTask) return "";
	if (!attribute.value) return "";

	if (compProps.datatype.id === "number") {
		return (
			gptSuggestionsStore.suggestionData[attribute.value?.id]
				// remove all non-numeric characters
				?.replace(/[^0-9.]/g, "")
				// remove trailing dot
				.replace(/\.$/, "") || ""
		);
	} else return gptSuggestionsStore.suggestionData[attribute.value?.id] || "";
});
const showSuggestion = computed(() => {
	return (
		suggestion.value.length > 0 &&
		questionsStore.tab === "open" &&
		suggestion.value !== "undefined" &&
		suggestion.value !== "unknown" &&
		suggestion.value !== String(compProps.value)
	);
});
const isMultiLineSuggestion = computed(() => {
	return suggestion.value.length > 50 || suggestion.value.includes("\n");
});

const suggestionChipColor = computed(() => {
	return compProps.questionColor || "#4db6ac";
});

const isFocused = ref(false);
const isUnknown = computed(() => compProps.value === "unknown" && !isFocused.value);

function setValue(v: any) {
	if (compProps.datatype.id === "number") {
		if (compProps.isArray && Array.isArray(v)) {
			v = v.map((item: string | number) => (item = Number(item)));
		} else {
			v = Number(v);
		}
	}
	if (compProps.datatype.id === "timestamp") {
		if (v === "undefined" || v === "unknown") {
			emits("set", v);
			return;
		}
		if (typeof v === "string") {
			v = new Date(v).getTime(); // convert local datetime string to milliseconds since epoch
		}
	}
	emits("set", v);
}

function onTextareaClear() {
	textareaValue.value = undefined;
	setValue("undefined");
}

function onTextareaEnter(e: KeyboardEvent) {
	if (!e.shiftKey) {
		e.preventDefault();
		(e.target as HTMLElement)?.blur();
	}
}

function setValueFromSuggestion() {
	if (suggestion.value === "?") {
		emits("set", "unknown");
		return;
	}

	if (compProps.datatype.id === "number") {
		emits(
			"set",
			compProps.isArray
				? [suggestion.value].map((item: string | number) => (item = Number(item)))
				: Number(suggestion.value),
		);
	} else if (compProps.datatype.id === "timestamp") {
		if (suggestion.value === "undefined" || suggestion.value === "unknown") {
			emits("set", suggestion.value);
			return;
		}
		// case 1) if suggestion is timestamp number / string (ms since epoch), convert to number
		const numericValue = Number(suggestion.value);
		if (!isNaN(numericValue) && suggestion.value.match(/^\d+$/)) {
			emits("set", numericValue);
			return;
		}
		// case 2) if suggestion is ISO datetime string or other parseable date string, convert to ms since epoch
		const parsedDate = new Date(suggestion.value);
		if (!isNaN(parsedDate.getTime())) {
			emits("set", parsedDate.getTime());
			return;
		}
	} else {
		emits("set", compProps.isArray ? [suggestion.value] : suggestion.value);
	}
}

let timeoutId: any;
function debounce(fn: () => void) {
	clearTimeout(timeoutId);
	timeoutId = setTimeout(fn, 300);
}

function onUpdateFocused(focused: boolean) {
	isFocused.value = focused;
	if (focused) {
		isKeyboardDateEntry.value = false;
		return;
	}

	isKeyboardDateEntry.value = false;
	if (focused) return;
	if (compProps.isArray) return;
	if (
		compProps.datatype.id === "string" ||
		compProps.datatype.id === "number" ||
		compProps.datatype.id === "date" ||
		compProps.datatype.id === "time" ||
		compProps.datatype.id === "timestamp"
	) {
		// For textarea (non-array string), use textareaValue
		if (!compProps.isArray && compProps.datatype.id === "string") {
			if (textareaValue.value === undefined || textareaValue.value === null || textareaValue.value === "") {
				setValue("undefined");
			} else {
				setValue(textareaValue.value);
			}
			return;
		}
		// For combobox (array or non-array date/time/number/timestamp), use lastTypedValue
		if (lastTypedValue.value === undefined) return;
		if (lastTypedValue.value === null || lastTypedValue.value === "" || lastTypedValue.value.length === 0) {
			setValue("undefined");
		} else {
			setValue(lastTypedValue.value);
		}
	}
}

function onComboboxEnter(e: KeyboardEvent) {
	if (compProps.isArray) return;
	isKeyboardDateEntry.value = false;
	setValue((e.target as HTMLInputElement)?.value);
}

function onComboboxKeydown() {
	if (compProps.isArray) return;
	if (compProps.datatype.id === "date" || compProps.datatype.id === "time" || compProps.datatype.id === "timestamp") {
		isKeyboardDateEntry.value = true;
	}
}

function onComboboxChange(e: Event) {
	if (compProps.isArray) return;
	if (compProps.datatype.id !== "date" && compProps.datatype.id !== "time" && compProps.datatype.id !== "timestamp")
		return;
	if (isKeyboardDateEntry.value) return;

	const value = (e.target as HTMLInputElement)?.value;
	if (value === undefined || value === null || value === "") {
		setValue("undefined");
		return;
	}

	setValue(value);
}

function dragStart(event: any) {
	questionsStore.suggestionDrag.isDragged = true;
	questionsStore.suggestionDrag.datatypeId = compProps.datatype.id;
	event.dataTransfer.setData("text/plain", suggestion.value);
}
function dragEnd(event: any) {
	questionsStore.suggestionDrag.isDragged = false;
}
function onDrop(event: any) {
	event.preventDefault();
	if (compProps.isArray) return;
	if (!datatypesAreCompatible()) return;

	// case string: allow everything (casted to string, use term if is symbolId)
	if (compProps.datatype.id === "string") {
		const text = event.dataTransfer.getData("text/plain");
		emits("set", text);
	}
	// case number: allow only numbers
	else if (compProps.datatype.id === "number") {
		const text = event.dataTransfer.getData("text/plain");
		emits("set", Number(text));
	}
	// else: take over the value
	else {
		const text = event.dataTransfer.getData("text/plain");
		emits("set", text);
	}
}
function onDragOver(event: any) {
	event.preventDefault();
}

function datatypesAreCompatible() {
	if (compProps.datatype.id === "string") return true;
	return compProps.datatype.id === questionsStore.suggestionDrag.datatypeId;
}

// returns the first concept view that contains the concept dataobject
function getView(conceptDataobjectId: string): ConceptView | undefined {
	const views = Object.values(graphStore.graph.nodes).filter((n) => n.type === "view") as ConceptView[];

	let visited: string[] = [];
	function traverseDataobject(id: string): boolean {
		if (id === conceptDataobjectId) return true;
		if (visited.includes(id)) return false;
		visited.push(id);
		let conceptDataobject = graphStore.concept.dataobject.byId(id);
		if (!conceptDataobject) return false;
		return (
			conceptDataobject.hasA.some((id) => traverseDataobject(id)) ||
			conceptDataobject.composedBy.some((id) => traverseDataobject(id))
		);
	}

	for (let view of views) {
		if (view.entryNodes.some((id) => traverseDataobject(id))) return view;
	}
}
function openEngineerDataobject(event: MouseEvent) {
	event.stopPropagation();
	const protocol = window.location.protocol === "http:" ? "http" : "https";
	const host = `engineer.notitia.dev`;
	const { id: domainId } = domainStore.domain;

	let conceptAttribute = graphStore.concept.attribute.byId(attribute.value?.concept || "");
	let conceptDataobject = graphStore.concept.dataobject.byId(conceptAttribute?.partOf || "");
	if (!conceptDataobject) return;
	let view: ConceptView | undefined = getView(conceptDataobject.id);
	if (!view) return;

	const url = `${protocol}://${host}/${domainId}/information-model/${view.id}?dataobject-id=${conceptDataobject.id}`;
	window.open(url, "_blank");
}

onMounted(() => {
	window.addEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.addEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
onUnmounted(() => {
	window.removeEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.removeEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});

// This looks very weird but it fixes the bug where the start disappears when user is entering values too fast using tab
const lastSearchValue = ref("");
function repairSearch(val: any) {
	if (!combobox.value) return;
	// edge case
	if (
		lastSearchValue.value.length > 0 &&
		displayedValue.value === undefined &&
		lastTypedValue.value === lastSearchValue.value
	) {
		combobox.value.search = lastSearchValue.value;
	}

	lastSearchValue.value = val;
}
</script>

<style scoped lang="scss">
.highlight-drop :first-child {
	background-color: rgb(var(--v-theme-primary)) !important;
}

:deep(.question-input-field--multi .v-field) {
	height: auto !important;
	min-height: 56px;
}

:deep(.question-input-field--multi .v-field__input) {
	align-items: flex-start;
	display: flex;
	flex-wrap: wrap;
	overflow: visible;
	padding-bottom: 8px;
	padding-top: 28px;
	row-gap: 4px;
}

.suggestion-chip {
	margin: 6px 8px 0 4px;
}

:deep(.v-input__details) {
	overflow: visible;
	padding-bottom: 4px;
}

.nta-border {
	animation: colorBreathing 2s infinite;
}
@keyframes colorBreathing {
	0% {
		box-shadow: 0 0 4px 2px var(--suggestion-chip-color, #4db6ac);
	}
	50% {
		box-shadow: 0 0 8px 4px var(--suggestion-chip-color, #4db6ac);
	}
	100% {
		box-shadow: 0 0 4px 2px var(--suggestion-chip-color, #4db6ac);
	}
}

.suggestion-box {
	background: rgb(var(--v-theme-on-surface-variant));
	border-radius: 16px;
	padding: 6px 12px;
	margin: 8px 0 4px 0;
	font-size: 0.875rem;
	line-height: 1.4;
	cursor: pointer;
	transition: box-shadow 0.2s ease;
	max-width: 100%;
	word-break: break-word;
	white-space: pre-wrap;
}
.suggestion-box:hover {
	background: rgb(var(--v-theme-on-surface-variant));
}

.text-input--unknown-active {
	filter: saturate(0.45);
	opacity: 0.55;
	transition: filter 0.15s ease-out, opacity 0.15s ease-out;
}

.novaBotAnimationInput ::v-deep .v-input__control {
	transition: box-shadow 0.3s ease, transform 0.3s ease;
	animation: rainbowGlow 2s ease-in-out;
}
@keyframes rainbowGlow {
	0% {
		box-shadow: 0 0 8px #f06292, 0 0 15px #f06292;
	}
	25% {
		box-shadow: 0 0 8px #ffb74d, 0 0 15px #ffb74d;
	}
	50% {
		box-shadow: 0 0 8px #81c784, 0 0 15px #81c784; /* Green */
	}
	75% {
		box-shadow: 0 0 8px #9575cd, 0 0 15px #9575cd; /* Blue */
	}
	100% {
		box-shadow: 0 0 8px #e57373, 0 0 15px #e57373; /* Indigo */
	}
}
</style>
